/**
 * 하단 상담 섹션의 앵커.
 *
 * 마케팅 레이아웃은 페이지 아래에 상담 폼(`Inquiry`)을 달고 그 섹션이 `id="inquiry"` 를
 * 갖는다. 헤더·모바일 서랍·메가패널의 상담 CTA 는 예전부터 이 앵커로 보냈다.
 *
 * **그런데 그 섹션이 없는 화면이 있다.** 로그인·회원가입 같은 앱 화면과, 섹션을 끄는
 * 요금 안내(`MarketingLayout showInquiry={false}`)다. 앵커는 대상이 없어도 아무 표시가
 * 나지 않아, 누른 사람은 버튼이 고장 난 줄도 모른다. 그래서 CTA 들은 **클릭 시점에**
 * 섹션이 있는지 보고, 없으면 같은 폼을 모달로 연다.
 *
 * 렌더 시점이 아니라 클릭 시점에 보는 이유: 헤더는 어느 페이지가 섹션을 켜는지 알 수
 * 없다. 알려면 레이아웃에서 헤더까지 prop 을 내려야 하는데, 헤더는 앱 화면에서도 쓰여서
 * 그 경로가 하나가 아니다. DOM 에 있는지 보는 편이 짧고, 틀릴 여지도 없다.
 */

export const INQUIRY_ANCHOR_ID = 'inquiry';
export const INQUIRY_ANCHOR = `#${INQUIRY_ANCHOR_ID}`;

/** 지금 화면에 하단 상담 폼이 있는가. 브라우저에서만 의미가 있다. */
export const hasInquirySection = (): boolean => typeof document !== 'undefined' && document.getElementById(INQUIRY_ANCHOR_ID) !== null;

/**
 * 하단 상담 폼이 **없는** 화면의 링크가 가리킬 곳.
 *
 * 그런 화면에서는 클릭을 가로채 모달을 열지만, 그건 JS 가 붙은 뒤의 이야기다. 하이드레이션
 * 전에 눌리면 링크의 `href` 만 남는다 — 거기가 `#inquiry` 면 URL 만 바뀌고 아무 일도
 * 일어나지 않는다. 그래서 **상담 섹션이 있는 페이지**로 보낸다. `/ask` 가 영구
 * 리다이렉트되는 곳이라 이 레포에서 "상담 창구"의 정본이기도 하다.
 *
 * **이건 no-JS 대책이 아니다.** 그 섹션의 비회원 폼은 선택 화면을 한 번 거쳐 나오고 제출도
 * 클라이언트 mutation 이라, 자바스크립트가 끝내 안 붙으면 거기서도 접수할 수 없다. 이 값이
 * 메우는 것은 **하이드레이션 전의 짧은 창**뿐이다 — 그 사이에 눌린 사람이 아무 일도 없는
 * 화면에 남는 대신, 곧 동작할 페이지로 옮겨 간다.
 *
 * 진짜 no-JS 접수는 서버로 POST 하는 경로가 있어야 하고, 그건 이 페이지 밖의 별건이다.
 */
export const INQUIRY_FALLBACK_HREF = '/faq#inquiry';

/**
 * 이 클릭을 가로채도 되는가.
 *
 * 상담 CTA 들은 링크로 그려 놓고 클릭을 가로채 모달을 연다. 그런데 **수정키를 누른 클릭과
 * 보조 버튼 클릭은 "새 탭에서 열어라"는 뜻이다** — 그것까지 막으면 링크처럼 생긴 것이
 * 링크처럼 동작하지 않는다. 가운데 클릭·컨텍스트 메뉴는 애초에 `click` 을 내지 않아
 * 그대로 통과하는데, 그 셋만 동작이 갈리면 입력 방식에 따라 결과가 달라진다.
 *
 * 그 경우에는 `href`(폼이 있는 화면)로 가게 둔다 — 모달을 새 탭에 띄울 수는 없으니,
 * 그쪽이 사용자가 기대한 것에 가장 가깝다.
 */
export const isPlainLeftClick = (e: { button: number; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean }): boolean =>
  e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
